/**
 * 테마 토글. 시맨틱 토큰(`bg-accent`·`text-foreground`)만 쓰므로 라이트·다크 양쪽에서
 * 자동으로 맞는다 — 이 컴포넌트 자신에는 `dark:` 가 **한 곳도 없어야 한다**.
 *
 * ## 왜 세 갈래(라이트·다크·시스템)인가
 *
 * 두 갈래만 두면 「OS 를 따른다」로 **돌아갈 수가 없다.** 한 번 고르는 순간 영원히
 * 고정되고, 폰이 밤에 다크로 바뀌어도 우리 화면만 밝게 남는다. 그래서 기본값이자
 * 복귀 지점으로 `system` 을 남긴다.
 *
 * `compact` 는 한 버튼으로 light↔dark 만 오간다(헤더 공간이 없는 곳). 그때도
 * 길게 누르거나 설정에서 `system` 으로 돌아갈 길이 있어야 하므로, 모바일 메뉴에는
 * 세 갈래 판을 둔다.
 */
import { cn } from '../lib/cn';
import { useTheme } from '../lib/use-theme';
import type { Theme } from '../lib/theme';

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

const OPTIONS: ReadonlyArray<{ value: Theme; label: string; Icon: typeof SunIcon }> = [
  { value: 'light', label: '라이트', Icon: SunIcon },
  { value: 'dark', label: '다크', Icon: MoonIcon },
  { value: 'system', label: '시스템', Icon: MonitorIcon },
];

/**
 * 한 버튼 토글. 지금 상태의 **반대** 아이콘을 보여 준다 — 버튼은 «누르면 무엇이
 * 되는가» 를 말해야 한다(지금 무엇인지는 화면이 이미 말하고 있다).
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolved, toggleTheme } = useTheme();
  const next = resolved === 'dark' ? '라이트' : '다크';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      title={`${next} 모드로 전환`}
      aria-label={`${next} 모드로 전환`}
      className={cn(
        'text-muted-foreground inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className
      )}
    >
      {resolved === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
    </button>
  );
}

/**
 * 세 갈래 세그먼트. 설정 화면·모바일 메뉴용이고, `system` 으로 **돌아갈 수 있는**
 * 유일한 경로다.
 */
export function ThemeSegmented({
  className,
  iconOnly = false,
  fullWidth = false,
}: {
  className?: string;
  /** 사이드바처럼 폭이 좁은 곳. 라벨을 지우고 `title`/`aria-label` 로 뜻을 남긴다. */
  iconOnly?: boolean;
  /** 세 칸을 균등 분할해 폭을 꽉 채운다. */
  fullWidth?: boolean;
}) {
  const { theme, setTheme } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="화면 테마"
      className={cn(
        'border-border bg-muted/40 items-center gap-1 rounded-lg border p-1',
        fullWidth ? 'flex w-full' : 'inline-flex',
        className
      )}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setTheme(value)}
            title={`${label} 모드`}
            aria-label={`${label} 모드`}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-colors',
              iconOnly ? 'px-2' : 'px-2.5',
              fullWidth && 'flex-1',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {iconOnly ? null : label}
          </button>
        );
      })}
    </div>
  );
}
